import GradientText from '@/common/components/gradient-text';
import FlexcarWordmark2024 from '@/icons/flexcar-wordmark-2024';
import { cn } from '@/shadcn-ui/utils';
import { colors } from '@/theme/colors';
import { getDynamicCurrency } from '@/util-conversion/currency';

interface CostComparisonCardItemProps {
  label: string;
  amount: number;
  variant: 'buying' | 'flexcar';
  suffix?: string;
}

function CostComparisonCardItem({
  label,
  amount,
  variant,
  suffix,
}: CostComparisonCardItemProps) {
  return (
    <div className="flex flex-col items-center gap-[4px] md:gap-[8px]">
      <p className="body2-tight text-[14px] leading-[20px] md:text-[16px] md:leading-[22px]">
        {label}
      </p>
      {variant === 'buying' ? (
        <p className="homepage-subhead1 text-[22px] font-[400] md:text-[24px]">
          {getDynamicCurrency(amount)}
          {suffix ? suffix : null}
        </p>
      ) : (
        <GradientText
          className="font-sans-tight text-[22px] leading-[32px] md:text-[24px]"
          gradient="linear-gradient(270deg, #00FFCD -54.58%, #2A00A5 100%)"
          variant="body-strong"
        >
          {getDynamicCurrency(amount)}
          {suffix ? suffix : null}
        </GradientText>
      )}
    </div>
  );
}

interface CostComparisonCardProps {
  variant: 'buying' | 'flexcar';
  upfrontPayment: number;
  carPaymentMonthlyCost: number;
  insuranceMonthlyCost: number;
}

export default function CostComparisonCard({
  variant,
  upfrontPayment,
  carPaymentMonthlyCost,
  insuranceMonthlyCost,
}: CostComparisonCardProps) {
  return (
    <div
      className={cn(
        'flex w-full flex-col items-center gap-[24px] rounded-[24px] px-0 py-[32px] md:w-[280px] md:gap-[32px] md:rounded-[32px] md:py-[40px]',
        {
          'bg-flexcar-grayishBlue': variant === 'buying',
          'bg-flexcar-neutral100 shadow-shadow25': variant === 'flexcar',
        }
      )}
    >
      {variant === 'buying' ? (
        <>
          <h3 className="header-sans-tight hidden h-[48px] items-center md:flex">
            Buying
          </h3>
          <h4 className="header-sans-tight flex h-[40px] items-center md:hidden">
            Buying
          </h4>
        </>
      ) : (
        <>
          <FlexcarWordmark2024
            fillPrimary={colors.neutral1}
            className="block h-[40px] w-[105px] flex-shrink-0 md:h-[48px] md:w-[128px]"
          />
        </>
      )}
      <div className="flex flex-col items-center gap-[24px]">
        {/* Upfront payment */}
        <CostComparisonCardItem
          label="Upfront payment"
          amount={upfrontPayment}
          variant={variant}
        />

        {/* Car payment */}
        <CostComparisonCardItem
          label="Car payment"
          amount={carPaymentMonthlyCost}
          variant={variant}
          suffix="/mo"
        />

        {/* Insurance & protection */}
        <CostComparisonCardItem
          label="Insurance & protection"
          amount={insuranceMonthlyCost}
          variant={variant}
          suffix="/mo"
        />
      </div>
    </div>
  );
}
